import Privilege from "../../entities/privilege";

/**
 * Privilege actions
 *
 * Call order:
 *
 * PRIVILEGE_SAVE_QUEUE:	adds the item to the save queue
 * --> PRIVILEGE_SAVE:	modifies the state
 * --> PRIVILEGE_SAVE_SUCCESS: if all went well
 * --> PRIVILEGE_SAVE_FAILURE:	if either the queue or local save failed
 *
 * Note that the only way to fail PRIVILEGE_SAVE_QUEUE is during the call to PRIVILEGE_SAVE,
 * as adding an item to the queue should never fail.
 *
 * OWA-1018: the reducer shows a queued save at PRIVILEGE_SAVE_QUEUE, marked pending, and only that
 * save's own outcome settles the mark: PRIVILEGE_SAVE_SUCCESS, PRIVILEGE_SAVE_REVERT (refused, or
 * never queued), a refusal the queue never retries, or PRIVILEGE_PENDING_CHECK (it left the queue).
 */

export const PRIVILEGE_FETCH_ONE_QUEUE: any = 'PRIVILEGE_FETCH_ONE_QUEUE';
export const PRIVILEGE_FETCH_SUCCESS: any = 'PRIVILEGE_FETCH_SUCCESS';
export const PRIVILEGE_FETCH_FAILURE: any = 'PRIVILEGE_FETCH_FAILURE';
export const PRIVILEGE_FETCH_ONE_SUCCESS: any = 'PRIVILEGE_FETCH_ONE_SUCCESS';
export const PRIVILEGE_FETCH_ONE_FAILURE: any = 'PRIVILEGE_FETCH_ONE_FAILURE';

export const PRIVILEGE_SAVE: any = 'PRIVILEGE_SAVE';
export const PRIVILEGE_SAVE_QUEUE: any = 'PRIVILEGE_SAVE_QUEUE';
export const PRIVILEGE_SAVE_SUCCESS: any = 'PRIVILEGE_SAVE_SUCCESS';
export const PRIVILEGE_SAVE_FAILURE: any = 'PRIVILEGE_SAVE_FAILURE';

export const PRIVILEGE_SAVE_MANY: any = 'PRIVILEGE_SAVE_MANY';
export const PRIVILEGE_SAVE_MANY_QUEUE: any = 'PRIVILEGE_SAVE_MANY_QUEUE';
export const PRIVILEGE_SAVE_MANY_SUCCESS: any = 'PRIVILEGE_SAVE_MANY_SUCCESS';
export const PRIVILEGE_SAVE_MANY_FAILURE: any = 'PRIVILEGE_SAVE_MANY_FAILURE';

export const PRIVILEGE_REMOVE: any = 'PRIVILEGE_REMOVE';
export const PRIVILEGE_REMOVE_QUEUE: any = 'PRIVILEGE_REMOVE_QUEUE';
export const PRIVILEGE_REMOVE_SUCCESS: any = 'PRIVILEGE_REMOVE_SUCCESS';
export const PRIVILEGE_REMOVE_FAILURE: any = 'PRIVILEGE_REMOVE_FAILURE';

export const PRIVILEGE_RESTORE: any = 'PRIVILEGE_RESTORE';
export const PRIVILEGE_RESTORE_QUEUE: any = 'PRIVILEGE_RESTORE_QUEUE';
export const PRIVILEGE_RESTORE_SUCCESS: any = 'PRIVILEGE_RESTORE_SUCCESS';
export const PRIVILEGE_RESTORE_FAILURE: any = 'PRIVILEGE_RESTORE_FAILURE';

export const PRIVILEGE_RESTORE_MANY: any = 'PRIVILEGE_RESTORE_MANY';
export const PRIVILEGE_REMOVE_MANY: any = 'PRIVILEGE_REMOVE_MANY';

// OWA-1018: settle this tab's pending save of a record
export const PRIVILEGE_SAVE_REVERT: any = 'PRIVILEGE_SAVE_REVERT';
export const PRIVILEGE_PENDING_CHECK: any = 'PRIVILEGE_PENDING_CHECK';

/* Fetch actions */
export const fetchPrivilegeSuccess = (privileges: Privilege[], partial: boolean, lastFetch: number) => ({type: PRIVILEGE_FETCH_SUCCESS, privileges, partial, lastFetch});
export const fetchPrivilegeFailure = (err: any) => {
		console.error('PrivilegeAction: fetchPrivilegeFailure', err);
		return {type: PRIVILEGE_FETCH_FAILURE, err};
};
export const fetchOnePrivilegeQueue = (privilegeId: string, sentAddedOn?: number) => ({type: PRIVILEGE_FETCH_ONE_QUEUE, privilegeId, sentAddedOn});
export const fetchOnePrivilegeSuccess = (privilege: Privilege | null, sentAddedOn?: number, privilegeId: string = privilege?.id) => ({type: PRIVILEGE_FETCH_ONE_SUCCESS, privilegeId, privilege, sentAddedOn});
export const fetchOnePrivilegeFailure = (id: string, err: any) => {
		console.error('PrivilegeAction: fetchOnePrivilegeFailure', err);
		return {type: PRIVILEGE_FETCH_ONE_FAILURE, id, err};
};

/* Save actions */
export const savePrivilege = (privilege: Privilege) => {
    // console.debug('⮂ PRIVILEGE_SAVE action', privilege?.id);
    return {type: PRIVILEGE_SAVE, privilege};
}
export const savePrivilegeQueue = (privilege: Privilege, onQueued?: (saved: boolean) => void) => {
    // console.debug('⮂ PRIVILEGE_SAVE_QUEUE action', privilege?.id);
    return {type: PRIVILEGE_SAVE_QUEUE, privilege, onQueued};
}
export const savePrivilegeSuccess = (privilege: Privilege, sentAddedOn?: number) => {
    // console.debug('⮂ PRIVILEGE_SAVE_SUCCESS action', privilege?.id);
    return {type: PRIVILEGE_SAVE_SUCCESS, privilege, sentAddedOn};
}
export const savePrivilegeFailure = (privilege: Privilege, status: number, message: string) => {
    console.warn(`⮂ PRIVILEGE_SAVE_FAILURE error saving privilege: HTTP ${status}: ${message}`, privilege?.id);
    return {type: PRIVILEGE_SAVE_FAILURE, privilege, status, message};
};

/* SaveMany actions */
export const saveManyPrivileges = (privileges: Privilege[]) => {
    // console.debug('⮂ PRIVILEGE_SAVE_MANY action', privileges?.map(privilege => privilege?.id).join(','));
    return {type: PRIVILEGE_SAVE_MANY, privileges};
}
export const saveManyPrivilegesQueue = (privileges: Privilege[], onQueued?: (saved: boolean) => void) => {
    // console.debug('⮂ PRIVILEGE_SAVE_MANY_QUEUE action', privileges?.map(privilege => privilege.id).join(','));
    return {type: PRIVILEGE_SAVE_MANY_QUEUE, privileges, onQueued};
}

/** `sentAddedOns`: the queue key each answer acknowledges, aligned with `privileges` (OWA-1018) */
export const saveManyPrivilegesSuccess = (privileges: Privilege[], sentAddedOns?: (number | undefined)[]) => {
    // console.debug('⮂ PRIVILEGE_SAVE_MANY_SUCCESS action', privileges?.map(privilege => privilege.id).join(','));
    return {type: PRIVILEGE_SAVE_MANY_SUCCESS, privileges, sentAddedOns};
}
export const saveManyPrivilegesFailure = (privileges: Privilege[], status: number, message: string) => {
		console.warn(`⮂ SAVE_MANY_PRIVILEGES_FAILURE Error saving privileges: HTTP ${status}: ${message}`, privileges?.map(privilege => privilege.id).join(','));
		return {type: PRIVILEGE_SAVE_MANY_FAILURE, privileges, status, message};
};

/* Remove actions */
export const removePrivilege = (privilege: Privilege) => ({ type: PRIVILEGE_REMOVE, privilege });
export const removePrivilegeQueue = (privilege: Privilege) => ({ type: PRIVILEGE_REMOVE_QUEUE, privilege });
export const removePrivilegeSuccess = (privilege: Privilege) => ({ type: PRIVILEGE_REMOVE_SUCCESS, privilege });
export const removePrivilegeFailure = (privilege: Privilege, status: number, message: string) => {
	console.error(`Redux: removePrivilegeFailure Error removing privilege: HTTP ${status}: ${message}`);
	return ({ type: PRIVILEGE_REMOVE_FAILURE, privilege, status, message });
};

/* Restore actions */
export const restorePrivilege = (privilege: Privilege) => ({ type: PRIVILEGE_RESTORE, privilege });
export const restorePrivilegeQueue = (privilege: Privilege) => ({ type: PRIVILEGE_RESTORE_QUEUE, privilege });
export const restorePrivilegeSuccess = (privilege: Privilege, sentAddedOn?: number) => ({ type: PRIVILEGE_RESTORE_SUCCESS, privilege, sentAddedOn });
export const restorePrivilegeFailure = (privilege: Privilege, status: number, message: string) => {
	console.error(`Redux: restorePrivilegeFailure Error restoring privilege: HTTP ${status}: ${message}`);
	return ({ type: PRIVILEGE_RESTORE_FAILURE, privilege, status, message });
};

/* RestoreMany actions */
export const restoreManyPrivileges = (privileges: Privilege[]) => ({ type: PRIVILEGE_RESTORE_MANY, privileges });

/* RemoveMany actions */
export const removeManyPrivileges = (privileges: Privilege[]) => ({ type: PRIVILEGE_REMOVE_MANY, privileges });

/* Pending save actions (OWA-1018) */
/**
 * The save `privilege` (queue key `sentAddedOn`, default its addedOn) will not be accepted: it was
 * refused, or never reached the queue. Its row returns to the latest accepted version, or to
 * `current` (the record as the server holds it) when that is newer; `keep` leaves the row shown.
 */
export const revertPrivilegeSave = (privilege: Privilege, options: {sentAddedOn?: number, current?: Privilege | null, keep?: boolean} = {}) =>
    ({type: PRIVILEGE_SAVE_REVERT, privilege, ...options});
/** processQueue read the shared queue at `readAt` (performance.now()): the live and the permanently refused queue keys */
export const checkPrivilegePendingSaves = (live: number[], refused: number[], readAt: number) =>
    ({type: PRIVILEGE_PENDING_CHECK, live, refused, readAt});